import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableModule, MatRowDef } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { NgSelectModule } from '@ng-select/ng-select';
import { ICreateExpeditionCondition } from './interfaces/create-expedition-condition.interface';
import ExpeditionConditionAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/masters/expedition-condition/expedition-condition.entity';
import { IUpdateExpeditionCondition } from './interfaces/update-expedition-condition.interface';
import { ExpeditionConditionService } from './expedition-condition.service';
import { CustomMatPaginatorIntl } from 'app/mock-api/common/customPaginatorIntl';
import { getCode } from 'app/utils/method';
import { Subscription } from 'rxjs';
import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { StatusFiltersComponent } from '../../filters/status-filters/status-filters.component';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { NoFiltersAvailableComponent } from 'app/common/components/no-filters-available/no-filters-available.component';



@Component({
  selector: 'app-expedition-condition',
  templateUrl: './expedition-condition.component.html',
  styleUrl: './expedition-condition.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatSlideToggle,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatRowDef,
    MatSidenavContainer,
    FormsModule,
    NgSelectModule,
    MatTabsModule,
    StatusFiltersComponent,
    HoverMenuDirective,
    FilterComponent,
    NoFiltersAvailableComponent
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
  ],
})
export class ExpeditionConditionComponent implements AfterViewInit, OnDestroy {
  getCode = getCode;
  displayedColumns: string[] = ["name", "status", "actions"];
  currentCreateExpeditionCondition: ICreateExpeditionCondition = this.initializeCreateExpeditionCondition();
  currentUpdateExpeditionCondition: ExpeditionConditionAttributes | undefined;

  dataSource: ExpeditionConditionAttributes[] = [];
  searchQuery: string = '';
  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 0;

  @ViewChild("editModal") editModal!: TemplateRef<any>;
  @ViewChild("addModal") addModal!: TemplateRef<any>;
  @ViewChild("deleteDialog") deleteDialog!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("searchInput") searchInput!: ElementRef;

  //BUSCADOR
  searchSubscription: Subscription;

  selectedStatuses: [] = [];

  constructor(
    private readonly expeditionConditionService: ExpeditionConditionService,
    private readonly searchService: SearchService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) { }

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.setupPaginationAndSorting();
    this.findAllWithCount();
  }



  private initializeCreateExpeditionCondition(): ICreateExpeditionCondition {
    return { name: "" , code: "", isItForShipping: false };
  }

  private setupPaginationAndSorting(): void {
    this.paginator.page.subscribe(() => this.findAllWithCount());
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    });
  }

  findAllWithCount(): void {
    const pageIndex = this.paginator.pageIndex + 1;
    const sortField = this.getSortField();
    const sortDirection: "ASC" | "DESC" = this.sort.direction === "asc" ? "ASC" : "DESC";

    this.expeditionConditionService
      .findAllWithCount(
        { currentPage: pageIndex, limitPerPage: this.pageSize, search: this.searchQuery, sort: sortField, sortDirection: sortDirection },
        this.selectedStatuses
      )
      .subscribe((data) => {
        this.dataSource = data?.data?.[0]?.data ?? [];
        this.totalItems = data?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.paginator.length = this.totalItems;
      });
  }

  private getSortField(): keyof ExpeditionConditionAttributes {
    const validSortFields: (keyof ExpeditionConditionAttributes)[] = ["name", "statusId"];
    return validSortFields.includes(this.sort.active as keyof ExpeditionConditionAttributes) ? (this.sort.active as keyof ExpeditionConditionAttributes) : "name";
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.findAllWithCount();
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value.trim();
    if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    }
  }

  public onHandleSearch(){
    this.searchSubscription = this.searchService.searchTerm$
      .subscribe(term => {
        this.searchQuery = term;
        if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
          this.paginator.pageIndex = 0;
          this.findAllWithCount();
        }
      });
  }

  trackByFn(index: number, item: ExpeditionConditionAttributes): string {
    return item._id.toString();
  }

  openCreateModal(): void {
    this.currentCreateExpeditionCondition = this.initializeCreateExpeditionCondition();
    this.dialog.open(this.addModal, {
      panelClass: "form-modal",
      disableClose: true, 
    })
    // .afterClosed()
    // .subscribe((result) => result && this.createExpeditionCondition());
  }

  createExpeditionCondition(): void {
    this.expeditionConditionService.create(this.currentCreateExpeditionCondition).subscribe({
      next: () => {
        this.snackBar.open('Registro creado con éxito', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
        this.dialog.closeAll();
      },
      error: (err) => {
        this.snackBar.open(err.error.message || 'Error al crear el registro', 'Cerrar', { duration: 3000 });
      }
    });
  }

  openEditModal(condition: ExpeditionConditionAttributes): void {
    this.currentUpdateExpeditionCondition = { ...condition };
    this.dialog.open(this.editModal, { panelClass: "form-modal", disableClose: true })
      .afterClosed()
      .subscribe((result) => result && this.updateExpeditionCondition(condition._id.toString()));
  }




  updateExpeditionCondition(id: string): void {
    if (this.currentUpdateExpeditionCondition) {
      const updatedExpeditionCondition: IUpdateExpeditionCondition = { ...this.currentUpdateExpeditionCondition
       };

      this.expeditionConditionService.update(id, updatedExpeditionCondition).subscribe({
        next: () => {
          this.snackBar.open('Registro actualizado con éxito', 'Cerrar', { duration: 3000 });
          this.findAllWithCount();
          this.dialog.closeAll();
          
        },
        error: (err) => {
          this.snackBar.open(err.error.message || 'Error al actualizar el registro', 'Cerrar', { duration: 3000 });
        }
      });
    }
  }

  onStatusChange(event: boolean, condition: ExpeditionConditionAttributes): void {
    const originalStatusId = condition.statusId;
    const updatedStatusId = event ? 1 : 0;
    const updatedExpeditionCondition = { ...condition, statusId: updatedStatusId };

    this.expeditionConditionService.update(condition._id.toString(), updatedExpeditionCondition).subscribe({
      next: () => {
        this.findAllWithCount();
      },
      error: () => {
        condition.statusId = originalStatusId; // Volver al estado original si falla
        this.snackBar.open('Error al actualizar el estado.', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
      }
    });
  }

  openDeleteDialog(condition: ExpeditionConditionAttributes): void {
    this.dialog
      .open(this.deleteDialog, { data: condition, panelClass: "delete-modal", disableClose:true })
      .afterClosed()
      .subscribe((result) => result && this.delete(condition._id.toString()));
  }

  delete(id: string): void {
    this.expeditionConditionService.remove(id).subscribe(() => {
      this.findAllWithCount();
      this.snackBar.open('Registro eliminado con éxito', 'Cerrar', { duration: 3000 });
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
